import React, { useEffect, useState } from 'react';

import { MinSec, NumberBox } from './styles';

interface IMinSecInputProps {
  /** Valor em segundos; null enquanto vazio. */
  seconds: number | null;
  onChange: (seconds: number | null) => void;
  label: string;
  hasError?: boolean;
  disabled?: boolean;
}

const split = (seconds: number | null) =>
  seconds === null
    ? { min: '', sec: '' }
    : {
        min: String(Math.floor(seconds / 60)),
        sec: String(seconds % 60).padStart(2, '0'),
      };

/**
 * Duração em "min : seg". Guarda o texto digitado localmente (para não
 * reescrever o campo a cada tecla) e emite segundos para o formulário.
 */
const MinSecInput: React.FC<IMinSecInputProps> = ({
  seconds,
  onChange,
  label,
  hasError,
  disabled,
}) => {
  const [text, setText] = useState(() => split(seconds));

  // Valor trocado de fora (ex.: "restaurar padrão"): reflete no campo.
  useEffect(() => {
    const local =
      text.min === '' && text.sec === ''
        ? null
        : Number(text.min || 0) * 60 + Number(text.sec || 0);
    if (local !== seconds) setText(split(seconds));
  }, [seconds]);

  const update = (part: 'min' | 'sec', raw: string) => {
    const digits = raw.replace(/\D/g, '').slice(0, part === 'min' ? 3 : 2);
    const next = { ...text, [part]: digits };
    setText(next);

    if (next.min === '' && next.sec === '') {
      onChange(null);
      return;
    }
    onChange(Number(next.min || 0) * 60 + Math.min(59, Number(next.sec || 0)));
  };

  return (
    <MinSec role="group" aria-label={label}>
      <NumberBox
        type="text"
        inputMode="numeric"
        aria-label={`${label} — minutos`}
        placeholder="0"
        $width="3.4rem"
        $hasError={hasError}
        disabled={disabled}
        value={text.min}
        onChange={e => update('min', e.target.value)}
      />
      :
      <NumberBox
        type="text"
        inputMode="numeric"
        aria-label={`${label} — segundos`}
        placeholder="00"
        $width="3.4rem"
        $hasError={hasError}
        disabled={disabled}
        value={text.sec}
        onChange={e => update('sec', e.target.value)}
        onBlur={() =>
          text.sec !== '' &&
          setText(prev => ({
            ...prev,
            sec: String(Math.min(59, Number(prev.sec))).padStart(2, '0'),
          }))
        }
      />
    </MinSec>
  );
};

export default MinSecInput;
